import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { employeesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { isPasswordValid, PASSWORD_REQUIREMENT_TEXT } from '../../utils/passwordPolicy';

const ChangePasswordPage = ({ onComplete }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ old: false, new: false, confirm: false });

  const userId = localStorage.getItem('userId');

  const validate = () => {
    const e = {};
    if (!form.oldPassword) e.oldPassword = 'Temporary password is required';
    if (!form.newPassword) {
      e.newPassword = 'New password is required';
    } else if (!isPasswordValid(form.newPassword)) {
      e.newPassword = PASSWORD_REQUIREMENT_TEXT;
    }
    if (!form.confirmPassword) {
      e.confirmPassword = 'Please confirm your new password';
    } else if (form.newPassword !== form.confirmPassword) {
      e.confirmPassword = 'Passwords do not match';
    }
    if (form.oldPassword && form.newPassword && form.oldPassword === form.newPassword) {
      e.newPassword = 'New password must differ from the temporary password';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!userId) {
      toast.error('Session error. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      await employeesAPI.updatePassword(userId, form.oldPassword, form.newPassword);
      localStorage.setItem('mustChangePassword', 'false');
      toast.success('Password updated successfully! Welcome aboard.');
      if (onComplete) onComplete();
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data || 'Failed to update password';
      toast.error(typeof msg === 'string' ? msg : 'Incorrect temporary password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 text-center" style={{ backgroundColor: '#1C4E6D' }}>
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 mb-3">
            <KeyRound size={28} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Set Your New Password</h1>
          <p className="text-sm text-blue-100 mt-1">
            You must change your temporary password before continuing.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <ShieldCheck size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              Your temporary password was sent to your email. Enter it below along with a new password of your choice.
            </p>
          </div>

          {/* Temporary Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password (from email)</label>
            <div className="relative">
              <input
                name="oldPassword"
                type={show.old ? 'text' : 'password'}
                value={form.oldPassword}
                onChange={handleChange}
                autoComplete="new-password"
                className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${
                  errors.oldPassword ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShow(p => ({ ...p, old: !p.old }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {show.old ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.oldPassword && <p className="text-red-600 text-xs mt-1">{errors.oldPassword}</p>}
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                name="newPassword"
                type={show.new ? 'text' : 'password'}
                value={form.newPassword}
                onChange={handleChange}
                autoComplete="new-password"
                className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${
                  errors.newPassword ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShow(p => ({ ...p, new: !p.new }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {show.new ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.newPassword && <p className="text-red-600 text-xs mt-1">{errors.newPassword}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <div className="relative">
              <input
                name="confirmPassword"
                type={show.confirm ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                className={`w-full px-3 py-2 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-400' : 'border-gray-300'
                }`}
              />
              <button
                type="button"
                onClick={() => setShow(p => ({ ...p, confirm: !p.confirm }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {show.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-600 text-xs mt-1">{errors.confirmPassword}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
            style={{ backgroundColor: '#1C4E6D' }}
            onMouseEnter={e => !loading && (e.currentTarget.style.backgroundColor = '#153a4f')}
            onMouseLeave={e => !loading && (e.currentTarget.style.backgroundColor = '#1C4E6D')}
          >
            {loading ? 'Updating...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
